import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import ProviderBottomNav from '../../components/common/ProviderBottomNav';
import { ArrowLeft, Clock, Calendar, Save, Coffee } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const TIME_OPTIONS = [];
for (let h = 6; h <= 22; h++) {
  for (let m = 0; m < 60; m += 30) {
    const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    TIME_OPTIONS.push(time);
  }
}

const formatTime12h = (time24) => {
  if (!time24) return '';
  const [hour, minute] = time24.split(':').map(Number);
  const period = hour < 12 ? 'AM' : 'PM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
};

export default function ProviderAvailability() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [availability, setAvailability] = useState({
    monday: { enabled: true, start_time: '09:00', end_time: '17:00', break_start: '12:00', break_end: '13:00' },
    tuesday: { enabled: true, start_time: '09:00', end_time: '17:00', break_start: '12:00', break_end: '13:00' },
    wednesday: { enabled: true, start_time: '09:00', end_time: '17:00', break_start: '12:00', break_end: '13:00' },
    thursday: { enabled: true, start_time: '09:00', end_time: '17:00', break_start: '12:00', break_end: '13:00' },
    friday: { enabled: true, start_time: '09:00', end_time: '17:00', break_start: '12:00', break_end: '13:00' },
    saturday: { enabled: false, start_time: '09:00', end_time: '14:00', break_start: null, break_end: null },
    sunday: { enabled: false, start_time: '09:00', end_time: '14:00', break_start: null, break_end: null },
    slot_duration_minutes: 60,
    buffer_minutes: 30
  });

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/provider/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setAvailability(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDayToggle = (dayKey) => {
    setAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled: !prev[dayKey]?.enabled
      }
    }));
  };

  const handleTimeChange = (dayKey, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value
      }
    }));
  };

  const handleSettingChange = (field, value) => {
    setAvailability(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/provider/availability`, availability, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Availability settings saved!');
    } catch (error) {
      toast.error('Failed to save availability');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const applyToAll = (dayKey) => {
    const sourceDay = availability[dayKey];
    const newAvailability = { ...availability };
    DAYS.forEach(day => {
      if (day.key !== dayKey) {
        newAvailability[day.key] = { ...sourceDay };
      }
    });
    setAvailability(newAvailability);
    toast.success(`Applied ${DAYS.find(d => d.key === dayKey)?.label}'s schedule to all days`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="availability-page">
      {/* Header */}
      <header className="bg-slate-900 pt-4 pb-6 px-4">
        <div className="flex items-center gap-3 mb-2">
          <button 
            onClick={() => navigate('/provider/profile')} 
            className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-white font-bold text-xl font-[Poppins]">Availability</h1>
            <p className="text-slate-400 text-sm">Set your working hours</p>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 max-w-lg mx-auto space-y-6">
        {/* General Settings */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-stone-100">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Booking Settings
          </h3>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-600">Time Slot Duration</Label>
              <select
                value={availability.slot_duration_minutes}
                onChange={(e) => handleSettingChange('slot_duration_minutes', Number(e.target.value))}
                className="w-full mt-1 p-2.5 border border-stone-200 rounded-xl bg-white text-sm"
                data-testid="slot-duration"
              >
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
            </div>
            
            <div>
              <Label className="text-sm text-slate-600">Buffer Between Bookings</Label>
              <select
                value={availability.buffer_minutes}
                onChange={(e) => handleSettingChange('buffer_minutes', Number(e.target.value))}
                className="w-full mt-1 p-2.5 border border-stone-200 rounded-xl bg-white text-sm"
                data-testid="buffer-minutes"
              >
                <option value={0}>No buffer</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Travel time is calculated automatically based on distance
              </p>
            </div>
          </div>
        </div>

        {/* Daily Schedule */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-stone-100">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-500" />
            Weekly Schedule
          </h3>
          
          <div className="space-y-4">
            {DAYS.map((day) => {
              const dayData = availability[day.key] || { enabled: false };
              return (
                <div 
                  key={day.key} 
                  className={`p-4 rounded-xl border transition-all ${
                    dayData.enabled 
                      ? 'bg-stone-50 border-stone-200' 
                      : 'bg-stone-100/50 border-stone-100'
                  }`}
                  data-testid={`day-${day.key}`}
                >
                  {/* Day Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={dayData.enabled}
                        onCheckedChange={() => handleDayToggle(day.key)}
                        data-testid={`toggle-${day.key}`}
                      />
                      <span className={`font-medium ${dayData.enabled ? 'text-slate-900' : 'text-slate-400'}`}>
                        {day.label}
                      </span>
                    </div>
                    {dayData.enabled && (
                      <button
                        onClick={() => applyToAll(day.key)}
                        className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                      >
                        Apply to all
                      </button>
                    )}
                  </div>
                  
                  {/* Time Selection */}
                  {dayData.enabled && (
                    <div className="space-y-3">
                      {/* Working Hours */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500">Start</Label>
                          <select
                            value={dayData.start_time || '09:00'}
                            onChange={(e) => handleTimeChange(day.key, 'start_time', e.target.value)}
                            className="w-full p-2 border border-stone-200 rounded-lg text-sm bg-white"
                            data-testid={`start-${day.key}`}
                          >
                            {TIME_OPTIONS.map(t => (
                              <option key={t} value={t}>{formatTime12h(t)}</option>
                            ))}
                          </select>
                        </div>
                        <span className="text-slate-400 pt-5">to</span>
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500">End</Label>
                          <select
                            value={dayData.end_time || '17:00'}
                            onChange={(e) => handleTimeChange(day.key, 'end_time', e.target.value)}
                            className="w-full p-2 border border-stone-200 rounded-lg text-sm bg-white"
                            data-testid={`end-${day.key}`}
                          >
                            {TIME_OPTIONS.map(t => (
                              <option key={t} value={t}>{formatTime12h(t)}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Break Time */}
                      <div className="border-t border-stone-200 pt-3 mt-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Coffee className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-500">Break Time (Optional)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <select
                              value={dayData.break_start || ''}
                              onChange={(e) => handleTimeChange(day.key, 'break_start', e.target.value || null)}
                              className="w-full p-2 border border-stone-200 rounded-lg text-sm bg-white"
                              data-testid={`break-start-${day.key}`}
                            >
                              <option value="">No break</option>
                              {TIME_OPTIONS.map(t => (
                                <option key={t} value={t}>{formatTime12h(t)}</option>
                              ))}
                            </select>
                          </div>
                          {dayData.break_start && (
                            <>
                              <span className="text-slate-400">to</span>
                              <div className="flex-1">
                                <select
                                  value={dayData.break_end || ''}
                                  onChange={(e) => handleTimeChange(day.key, 'break_end', e.target.value || null)}
                                  className="w-full p-2 border border-stone-200 rounded-lg text-sm bg-white"
                                  data-testid={`break-end-${day.key}`}
                                >
                                  {TIME_OPTIONS.map(t => (
                                    <option key={t} value={t}>{formatTime12h(t)}</option>
                                  ))}
                                </select>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Save Button */}
        <Button
          className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 h-12 rounded-xl font-semibold"
          onClick={handleSave}
          disabled={isSaving}
          data-testid="save-availability-btn"
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
              Saving...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-5 h-5" />
              Save Availability
            </span>
          )}
        </Button>
      </main>

      <ProviderBottomNav />
    </div>
  );
}
